/**
 * Tenant SWR layer.
 *
 * Every GET-shaped procedure goes through `useTenantQuery`. The cache key
 * is `[endpoint, userId, activeGroupId, ...params]`:
 *
 *   - `userId` (Clerk's stable identity) scopes the cache per signed-in
 *     user. It flips on sign-in / sign-out only — never on token
 *     rotation — so a fresh Regta JWT every ~14 min does not invalidate
 *     anything, but a different user signing into the same tab gets a
 *     clean cache namespace automatically;
 *   - switching the active group flips the key → SWR auto-fetches the
 *     newly-scoped data, the prior cache entry stays addressable but
 *     no longer rendered (so leaving and coming back is instant);
 *   - the `endpoint` string is opaque and stable, used by `invalidate()`
 *     to revalidate every key derived from it after a write.
 *
 * The Regta JWT is deliberately NOT part of the cache key. Baking it in
 * would invalidate every cached query on every rotation and, worse,
 * briefly flip `data` to `undefined` for components like
 * ContractEditorPage — which unmounts on `!contract`, taking unsaved
 * form state with it. The fetcher reads the latest token from a ref
 * each call instead.
 *
 * The fetcher sets the `X-Regta-Active-Group` header from the cache key
 * itself before issuing the request. This guarantees the header value
 * matches the cache key on every individual fetch — no chance of
 * module-state drift between renders.
 *
 * Writes call `invalidate('endpoint.name')`; the matcher revalidates
 * every key whose first segment matches.
 */

import useSWR, {
  useSWRConfig,
  type SWRResponse,
  type SWRConfiguration,
} from 'swr';
import { setActiveGroupHeader } from './api.js';
import { useActiveGroup } from './active-group-context.js';
import { useTenantSession } from './auth-context.js';

/** Cache key shape — exported so tests/callers can reason about it. */
export type TenantQueryKey = readonly [
  endpoint: string,
  userId: string,
  activeGroupId: string | null,
  ...params: ReadonlyArray<unknown>,
];

/**
 * Hook: subscribe to a tenant-scoped GET.
 *
 * @param endpoint  Stable identifier (e.g. `'contracts.list'`,
 *                  `'contracts.get'`). Used as the first segment of the
 *                  cache key — `invalidate(endpoint)` matches all keys
 *                  starting with this value.
 * @param fetcher   Function that performs the actual RPC call. Receives
 *                  the validated token plus any extra params declared in
 *                  `params`. The active-group header is set
 *                  automatically before invocation.
 * @param params    Extra arguments that should be part of the cache key
 *                  (e.g. a contract id for `contracts.get`). Use stable
 *                  values; do not pass arrays/objects by reference.
 * @param config    Pass-through to SWR for options like `refreshInterval`.
 */
export function useTenantQuery<TOut>(
  endpoint: string,
  fetcher: (...params: ReadonlyArray<unknown>) => Promise<TOut>,
  params: ReadonlyArray<string | number | boolean | null | undefined> = [],
  config?: SWRConfiguration<TOut> & {
    /**
     * Set true for tenant-wide reads (e.g. `users.list`, `groups.list`,
     * `groups.members`) that the server doesn't narrow by the
     * active-group header. The key omits `activeGroupId`, so flipping
     * groups doesn't trigger an unnecessary refetch.
     */
    ignoreActiveGroup?: boolean;
    /**
     * Conditionally enable the query. When `false`, the cache key
     * becomes `null` so SWR doesn't fetch. Flipping back to `true`
     * changes the key and SWR fetches automatically. Prefer this over
     * `isPaused`, which doesn't react to its own predicate.
     */
    enabled?: boolean;
  },
): SWRResponse<TOut> {
  // `isReady` flips true once the initial Regta JWT mint + whoami
  // succeeds. The actual token is read by the api helper from the
  // module-level provider at call time, so it's not part of the
  // cache key — token rotation must not re-shape any key.
  const { isReady, userId } = useTenantSession();
  const { activeGroupId } = useActiveGroup();
  const ignore = config?.ignoreActiveGroup ?? false;
  const enabled = config?.enabled ?? true;

  const keyGroup = ignore ? null : activeGroupId;
  const key: TenantQueryKey | null =
    isReady && userId && enabled
      ? ([endpoint, userId, keyGroup, ...params] as const)
      : null;

  return useSWR<TOut>(
    key,
    async (k: TenantQueryKey) => {
      const [, , groupId, ...rest] = k;
      // For tenant-wide reads, send the header anyway so the server can
      // log/observe it but won't filter on it (procedure decides).
      setActiveGroupHeader(ignore ? activeGroupId : groupId);
      return fetcher(...rest);
    },
    config,
  );
}

/**
 * Imperative cache invalidation. Use after a write to bust every
 * relevant query.
 *
 *   await tenantApi.contracts.create_from_template(…);
 *   await invalidate('contracts.list');           // refetch the list
 *   await invalidate('properties.get', propId);   // and any open detail
 *
 * Accepts either a bare endpoint name (matches all keys starting with
 * that endpoint) or an exact key prefix (matches only keys with those
 * leading segments).
 */
function keyMatcher(endpoint: string, prefix: ReadonlyArray<unknown>) {
  return (key: unknown): boolean => {
    if (!Array.isArray(key)) return false;
    if (key[0] !== endpoint) return false;
    // params start at index 3 ([endpoint, userId, groupId, ...params])
    for (let i = 0; i < prefix.length; i++) {
      if (key[3 + i] !== prefix[i]) return false;
    }
    return true;
  };
}

export function useInvalidate() {
  const { mutate } = useSWRConfig();
  return (endpoint: string, ...prefix: ReadonlyArray<unknown>) =>
    mutate(
      keyMatcher(endpoint, prefix),
      undefined,
      // Keep the stale value in the cache while the background refetch
      // runs — otherwise SWR writes `undefined` into the cache first
      // (populateCache defaults to true), components like the contract
      // editor briefly fall through to their `loading…` branch, the
      // document collapses, and the window scroll position is clamped
      // to 0. populateCache:false skips that write and just revalidates.
      { revalidate: true, populateCache: false },
    );
}

/**
 * Remove cached entries outright, without revalidating. For resources
 * that no longer exist (a deleted contract): invalidate() would refetch
 * a 404 while SWR keeps the stale data rendered, so browser-Back would
 * show the dead resource as alive.
 */
export function useDropCached() {
  const { mutate } = useSWRConfig();
  return (endpoint: string, ...prefix: ReadonlyArray<unknown>) =>
    mutate(keyMatcher(endpoint, prefix), undefined, {
      revalidate: false,
      populateCache: true,
    });
}

